import { useCallback, useMemo } from 'react';
import { isDraftItem, getCardTitle, getBulkProjectName, getListingMode } from '../utils/helpers';

// Filter items by draft/completed status
const filterByStatus = (items, statuses) => {
  if (statuses.length === 0) return [];
  if (statuses.length >= 2) return items;
  return items.filter(item => {
    const draft = isDraftItem(item);
    if (statuses.includes('draft') && draft) return true;
    if (statuses.includes('completed') && !draft) return true;
    return false;
  });
};

// Filter items by listing mode (new/existing)
const filterByMode = (items, modes) => {
  if (modes.length === 0) return items;
  return items.filter(item => {
    const mode = getListingMode(item).toLowerCase();
    if (modes.includes('new') && mode.includes('new')) return true;
    if (modes.includes('existing') && mode.includes('existing')) return true;
    return false;
  });
};

const useFilteredProjects = ({
  generations,
  bulkGenerations,
  searchTerm,
  selectedStatuses,
  selectedCategories,
  selectedModes,
  selectedSortOption,
  selectedProjectTypes,
}) => {
  const matchesSearch = useCallback((gen, term) => {
    if (!term) return true;
    const lowerTerm = term.toLowerCase();
    const title = getCardTitle(gen).toLowerCase();
    const product = gen.product || {};
    const searchFields = [
      title,
      (product.brand_name || '').toLowerCase(),
      (product.category || '').toLowerCase(),
      (product.model_number || '').toLowerCase(),
      (product.description || '').toLowerCase()
    ];
    return searchFields.some(field => field.includes(lowerTerm));
  }, []);

  const sortGenerations = useCallback((a, b, sortOption) => {
    switch (sortOption) {
      case 'name':
        return getCardTitle(a).localeCompare(getCardTitle(b));
      case 'date-asc':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'date-desc':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  }, []);

  const filteredGenerations = useMemo(() => {
    if (!selectedProjectTypes.includes('single')) return [];

    let filtered = [...generations];
    if (searchTerm) {
      filtered = filtered.filter(gen => matchesSearch(gen, searchTerm));
    }
    filtered = filterByStatus(filtered, selectedStatuses);
    filtered = filterByMode(filtered, selectedModes);
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(gen => {
        const product = gen.product || {};
        return selectedCategories.includes(product.category || '');
      });
    }
    filtered.sort((a, b) => sortGenerations(a, b, selectedSortOption));
    return filtered;
  }, [generations, searchTerm, selectedProjectTypes, selectedStatuses, selectedModes, selectedCategories, selectedSortOption, matchesSearch, sortGenerations]);

  const filteredBulkGenerations = useMemo(() => {
    if (!selectedProjectTypes.includes('bulk')) return [];

    const seenIds = new Set();
    let filtered = bulkGenerations.filter(bulk => {
      if (seenIds.has(bulk._id)) return false;
      seenIds.add(bulk._id);
      return true;
    });
    filtered = filterByStatus(filtered, selectedStatuses);
    filtered = filterByMode(filtered, selectedModes);
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(bulk =>
        getBulkProjectName(bulk).toLowerCase().includes(lowerTerm)
      );
    }
    filtered.sort((a, b) => {
      if (selectedSortOption === 'name') {
        return getBulkProjectName(a).localeCompare(getBulkProjectName(b));
      }
      const isAsc = selectedSortOption === 'date-asc';
      const comparison = new Date(a.created_at) - new Date(b.created_at);
      return isAsc ? comparison : -comparison;
    });
    return filtered;
  }, [bulkGenerations, searchTerm, selectedProjectTypes, selectedStatuses, selectedModes, selectedSortOption]);

  const allFilteredProjects = useMemo(() => {
    const combined = [];
    if (selectedProjectTypes.includes('single')) {
      filteredGenerations.forEach(gen => combined.push({ ...gen, projectType: 'single' }));
    }
    if (selectedProjectTypes.includes('bulk')) {
      filteredBulkGenerations.forEach(bulk => combined.push({ ...bulk, projectType: 'bulk' }));
    }

    const getSortDate = (item) => {
      const d = item.created_at;
      if (!d) return 0;
      const ts = new Date(d).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };
    combined.sort((a, b) => {
      switch (selectedSortOption) {
        case 'name': {
          const nameA = a.projectType === 'single' ? getCardTitle(a) : getBulkProjectName(a);
          const nameB = b.projectType === 'single' ? getCardTitle(b) : getBulkProjectName(b);
          return nameA.localeCompare(nameB);
        }
        case 'date-asc':
          return getSortDate(a) - getSortDate(b);
        case 'date-desc':
        default:
          return getSortDate(b) - getSortDate(a);
      }
    });
    return combined;
  }, [filteredGenerations, filteredBulkGenerations, selectedProjectTypes, selectedSortOption]);

  const getUniqueCategories = useMemo(() => {
    return () => {
      const categories = generations
        .map(gen => gen.product?.category)
        .filter(Boolean);
      return [...new Set(categories)];
    };
  }, [generations]);

  return {
    filteredGenerations,
    filteredBulkGenerations,
    allFilteredProjects,
    getUniqueCategories,
  };
};

export default useFilteredProjects;
